import React, { useState } from 'react';
import { QrCode } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import communityRegistryApi from '../../services/communityRegistryApi';

interface Props {
  open: boolean;
  onClose: () => void;
}

const GenerateQRCodesModal: React.FC<Props> = ({ open, onClose }) => {
  const [count, setCount] = useState('12');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  const reset = () => {
    setCount('12');
    setError(null);
    setDone(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const n = Number(count);
  const canSubmit = Number.isInteger(n) && n >= 1 && n <= 200 && !submitting;

  const handleGenerate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const codes = await communityRegistryApi.generateRegistryCodes(n);
      const blob = await communityRegistryApi.downloadRegistryCodesPdf(codes.map((c) => c.code));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registry-qr-codes-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setDone(codes.length);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to generate QR codes');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            Generate QR Codes
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Generates unique registry codes and downloads a printable PDF (6 QR codes per page).
          Each code can be scanned once in the field to register a household.
        </p>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {done !== null && (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700">
            Generated {done} unique code{done === 1 ? '' : 's'} — PDF download started.
          </div>
        )}

        <div className="space-y-1">
          <Label htmlFor="qr-count">Number of QR codes (1–200)</Label>
          <Input
            id="qr-count"
            type="number"
            min={1}
            max={200}
            value={count}
            onChange={(e) => setCount(e.target.value)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Close
          </Button>
          <Button onClick={handleGenerate} disabled={!canSubmit}>
            {submitting ? 'Generating…' : 'Generate & Download PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateQRCodesModal;
